import type { ReactNode } from "react";

import { useHeaderContext } from "@/contexts/HeaderContext";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { MEDIA_QUERY } from "@/utils/mediaQueries";

interface ResponsiveScreenLayoutProps {
  children: ReactNode;
  button?: ReactNode;
  maxWidth?: 390 | 420;
  buttonVisibility?: "mobile-only" | "desktop-only" | "both";
}

/**
 * Unified responsive layout component for Auth0 custom screens.
 *
 * Features:
 * - Automatically adjusts alignment based on viewport (start on mobile, center on desktop)
 * - Dynamic mobile height based on PropHeroHeader visibility via HeaderContext
 * - Optional button with flexible visibility controls
 * - Mobile: Button sticks to bottom, content is scrollable
 * - Desktop: Button flows naturally with content
 *
 * Mobile Height Behavior:
 * - When PropHeroHeader is visible (hasScreenHeaderWithBack=false): calc(100dvh - 6.5rem)
 * - When PropHeroHeader is hidden (hasScreenHeaderWithBack=true): calc(100dvh - 3.5rem)
 *
 * @param children - Main content to display
 * @param button - Optional button/action element (e.g., submit button, navigation link)
 * @param maxWidth - Maximum width of content container (default: 390)
 * @param buttonVisibility - Controls button visibility across viewports (default: "both")
 *   - "mobile-only": Button only appears on mobile (≤1024px)
 *   - "desktop-only": Button only appears on desktop (>1024px)
 *   - "both": Button appears on all viewports
 */
function ResponsiveScreenLayout({
  children,
  button,
  maxWidth = 390,
  buttonVisibility = "both",
}: ResponsiveScreenLayoutProps) {
  const isMobile = useMediaQuery(MEDIA_QUERY.mobile);
  const { hasScreenHeaderWithBack } = useHeaderContext();
  const maxWidthClass = maxWidth === 420 ? "max-w-[420px]" : "max-w-[390px]";

  // Dynamic height: viewport minus outer padding (pt-10 + pb-4 = 3.5rem),
  // and minus the PropHeroHeader height (~3rem) when it's visible.
  const mobileHeightClass = hasScreenHeaderWithBack
    ? "h-[calc(100dvh-3.5rem)]"
    : "h-[calc(100dvh-6.5rem)]";

  // Determine if button should be rendered based on viewport and visibility setting
  const shouldShowButton =
    button &&
    (buttonVisibility === "both" ||
      (buttonVisibility === "mobile-only" && isMobile) ||
      (buttonVisibility === "desktop-only" && !isMobile));

  // Mobile layout: button at bottom, content scrollable
  if (isMobile && button) {
    return (
      <div className="w-full flex justify-center p-4 pt-10">
        <div
          className={`w-full ${maxWidthClass} flex flex-col ${mobileHeightClass}`}
        >
          <div className="flex-1 flex flex-col gap-6 w-full overflow-auto min-h-0">
            {children}
          </div>
          {shouldShowButton && (
            <div className="shrink-0 pt-4 w-full flex justify-center">
              {button}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop layout or mobile without button: centered with natural flow
  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4">
      <div
        className={`w-full ${maxWidthClass} flex flex-col gap-6 ${
          isMobile ? "items-start" : "items-center"
        }`}
      >
        {children}
        {shouldShowButton && button}
      </div>
    </div>
  );
}

export default ResponsiveScreenLayout;
